import React, { type ChangeEvent, useMemo, type ReactNode, useCallback } from 'react';
import { Field, ErrorMessage } from '@atlaskit/form';
import { Box, xcss } from '@atlaskit/primitives';
import TextField from '@atlaskit/textfield';
import { useIntl } from '@atlassian/jira-intl';

import { PROJECT_KEY_LEARN_MORE_LINK } from '../../../common/constants/project';
import { InfoButton } from '../../../common/ui/info-button';
import messages from './messages';

export type Props = {
	projectKey: string;
	onProjectKeyChange: (value: string) => void;
	projectKeyValidationError?: string;
};

export const ProjectKeyField = ({
	projectKey,
	onProjectKeyChange,
	projectKeyValidationError,
}: Props) => {
	const { formatMessage } = useIntl();

	const projectKeyFieldLabel = useMemo(
		() => (
			<Box
				xcss={labelWrapperStyle}
				testId="project-create-importer-upload-flow.ui.project-create-screen.project-key-field.label-wrapper"
			>
				{formatMessage(messages.projectKey)}
				<InfoButton
					description={formatMessage(messages.projectKeyDescription, {
						link: (content: ReactNode) => (
							<a href={PROJECT_KEY_LEARN_MORE_LINK} target="_blank">
								{content}
							</a>
						),
					})}
				/>
			</Box>
		),
		[formatMessage],
	);

	const onChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const newKeyValue = e.target.value ? e.target.value.toUpperCase() : e.target.value;
			onProjectKeyChange(newKeyValue);
		},
		[onProjectKeyChange],
	);

	return (
		<Box xcss={projectKeyWrapperStyle}>
			<Field label={projectKeyFieldLabel} name="key">
				{({ fieldProps }) => (
					<>
						<TextField
							testId="project-create-importer-upload-flow.ui.project-create-screen.project-key-field.project-key-field"
							{...fieldProps}
							value={projectKey}
							onChange={onChange}
						/>
						{projectKeyValidationError !== '' && (
							<ErrorMessage>{projectKeyValidationError}</ErrorMessage>
						)}
					</>
				)}
			</Field>
		</Box>
	);
};

const projectKeyWrapperStyle = xcss({
	width: '168px',
});

const labelWrapperStyle = xcss({
	display: 'flex',
	alignItems: 'center',
	flexDirection: 'row',
	columnGap: 'space.050',
});
